import React from "react";

import { withStyles } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";

import {
  AuditLogDataTypes,
  AuditLogFieldNames,
  AuditLogTypes,
  CommonAuditLogFieldNames,
} from "constants/auditLog";
import { ValueSource } from "constants/ruleBooks";

import { formatDollarNumberWithExtraDecimals, formatWeightKg } from "lib";

import {
  formatUTCToLocalDateString,
  formatUTCToLocalDateTimeString,
} from "lib/timeFormats";

import { Link } from "components/Link";
import { openAuditLogModal } from "lib/navigation";

export interface AuditLogRelatedValue {
  value?: string | number | string[] | null;
  display?: string;
  source?: string;
}

export type AuditLogFieldValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | AuditLogRelatedValue;

interface GlCodeValue {
  source: string;
  value: string;
}

export const FieldNameTableCell = withStyles(() => ({
  root: {
    textAlign: "right" as const,
    width: "30%",
  },
}))(TableCell);

function formatGlCode(glCode: GlCodeValue): React.ReactNode {
  if (glCode.source === ValueSource.CONSTANT) {
    return glCode.value;
  } else if (glCode.source === ValueSource.FIELD) {
    return "Determined from source data";
  }
  return "-";
}

type AuditLogValueFormatter = (value: never) => React.ReactNode;

const AuditLogTypeFormatters: Partial<
  Record<AuditLogDataTypes, AuditLogValueFormatter>
> = {
  [AuditLogDataTypes.WEIGHT_GRAMS]: formatWeightKg,
  [AuditLogDataTypes.CURRENCY_CENTS]: (currencyCents: number) =>
    `${formatDollarNumberWithExtraDecimals(currencyCents / 100)}`,
  [AuditLogDataTypes.UTC_DATE_TIME]: formatUTCToLocalDateTimeString,
  [AuditLogDataTypes.UTC_DATE]: formatUTCToLocalDateString,
  [AuditLogDataTypes.GL_CODE]: formatGlCode,
};

export const AuditLogFieldTypes: Partial<
  Record<AuditLogTypes, Record<string, AuditLogDataTypes>>
> = {
  [AuditLogTypes.BUSINESS]: {
    business_registration: AuditLogDataTypes.RELATED_ID,
  },
  [AuditLogTypes.BUSINESS_REGISTRATION]: {
    gst_registered_from: AuditLogDataTypes.UTC_DATE,
    gst_registered_to: AuditLogDataTypes.UTC_DATE,
    abr_last_checked: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.SALE_LOT]: {
    total_price_cents: AuditLogDataTypes.CURRENCY_CENTS,
    total_mass_grams: AuditLogDataTypes.WEIGHT_GRAMS,
    first_sold: AuditLogDataTypes.UTC_DATE_TIME,
    estimated_average_mass_grams: AuditLogDataTypes.WEIGHT_GRAMS,
  },
  [AuditLogTypes.CONSIGNMENT]: {
    earliest_pickup_date: AuditLogDataTypes.UTC_DATE_TIME,
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.SCAN]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
    time_weighed: AuditLogDataTypes.UTC_DATE_TIME,
    total_mass_grams: AuditLogDataTypes.WEIGHT_GRAMS,
    nlis_status: AuditLogDataTypes.RELATED_ID,
  },
  [AuditLogTypes.NLIS_STATUS]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
    last_nlis_check: AuditLogDataTypes.UTC_DATE_TIME,
    scan: AuditLogDataTypes.RELATED_ID,
  },
  [AuditLogTypes.DEPLOYMENT_BUSINESS]: {
    business_registration: AuditLogDataTypes.RELATED_ID,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    deployment_business_email_recipient: AuditLogDataTypes.RELATED_ID,
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.CLEARING_SALE_ATTRIBUTES]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.DRAFTING_ATTRIBUTES]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
    max_mass_grams: AuditLogDataTypes.WEIGHT_GRAMS,
    min_mass_grams: AuditLogDataTypes.WEIGHT_GRAMS,
    est_carcass_weight: AuditLogDataTypes.WEIGHT_GRAMS,
  },
  [AuditLogTypes.RULE]: {
    gl_code: AuditLogDataTypes.GL_CODE,
  },
  [AuditLogTypes.RECEIVAL_LOT]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.SALEYARD_BUSINESS]: {
    business_registration: AuditLogDataTypes.RELATED_ID,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    saleyard_business_email_recipient: AuditLogDataTypes.RELATED_ID,
  },
  [AuditLogTypes.PEN_SCAN_LOT]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.LEDGER_ENTRY]: {
    subtotal: AuditLogDataTypes.CURRENCY_CENTS,
    tax_amount: AuditLogDataTypes.CURRENCY_CENTS,
    unit_amount: AuditLogDataTypes.CURRENCY_CENTS,
  },
  [AuditLogTypes.WEIGH_LOT]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
  },
  [AuditLogTypes.WEIGH_LOT_SCAN]: {
    last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    created: AuditLogDataTypes.UTC_DATE_TIME,
    database_last_modified: AuditLogDataTypes.UTC_DATE_TIME,
    database_created: AuditLogDataTypes.UTC_DATE_TIME,
  },
};

// Handing a relation object to React throws "Objects are not valid as a React child".
export function renderUnformatted(
  fieldValue: AuditLogFieldValue,
): React.ReactNode {
  if (typeof fieldValue === "object") {
    return fieldValue?.display || "-";
  }
  if (typeof fieldValue === "boolean") {
    return fieldValue ? "Yes" : "No";
  }
  return fieldValue;
}

interface FieldValueProps {
  dataType: AuditLogTypes;
  fieldName: string;
  fieldValue: AuditLogFieldValue;
}

export const FieldValue = ({
  dataType,
  fieldName,
  fieldValue,
}: FieldValueProps): React.ReactNode => {
  const formatterType = AuditLogFieldTypes[dataType]?.[fieldName];

  if (!formatterType) {
    return renderUnformatted(fieldValue);
  }

  if (formatterType === AuditLogDataTypes.RELATED_ID) {
    // FK fields from the API come back as {value: id, display: "..."} objects.
    // Extract the raw ID so the audit log URL is /v2/auditlog/<type>/<uuid>/ not [object Object].
    const idValue =
      typeof fieldValue === "object" && fieldValue !== null
        ? fieldValue.value ?? fieldValue
        : fieldValue;
    return (
      <Link
        onClick={() => {
          openAuditLogModal(fieldName, idValue, window.location.hash);
        }}
      >
        View Changes
      </Link>
    );
  }

  const formatter = AuditLogTypeFormatters[formatterType];
  if (typeof formatter !== "function") {
    return renderUnformatted(fieldValue);
  }

  try {
    return formatter(fieldValue as never);
  } catch (e) {
    // It should all work... but if it's blank/unexpected/weird better to show something badly formatted than nothing,
    // or raise an error.
    return renderUnformatted(fieldValue);
  }
};

interface FieldNameProps {
  dataType: AuditLogTypes;
  fieldName: string;
}

export const FieldName = ({
  dataType,
  fieldName,
}: FieldNameProps): React.ReactNode => {
  return (
    AuditLogFieldNames[dataType]?.[fieldName] ||
    CommonAuditLogFieldNames[
      fieldName as keyof typeof CommonAuditLogFieldNames
    ] ||
    fieldName
  );
};
